import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MoreVertical, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Trash2,
  ArrowUpDown,
  Eye,
  Edit,
  Megaphone,
  Tags,
  X,
  MapPin
} from 'lucide-react';
import Modal from './Modal';
import moment from 'moment';
import CampaignViewModal from './CampaignViewModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { campaignsRequest } from '../../requests/vendor/CampaignsRequest';
import DeleteModal from './DeleteModal';
import { useSelector } from "react-redux";

const calculateDropdownPosition = (buttonRect, dropdownHeight = 200) => {
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const spaceBelow = viewportHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;

  // Position dropdown relative to the button
  let top = buttonRect.bottom + 5;
  let left = buttonRect.right - 180; // Align to right edge of button

  // Check if dropdown would go below viewport
  if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
    top = buttonRect.top - dropdownHeight - 5;
  }

  // Ensure dropdown doesn't go off screen to the right
  if (left + 180 > viewportWidth) {
    left = viewportWidth - 190;
  }

  // Ensure dropdown doesn't go off screen to the left
  if (left < 10) {
    left = 10;
  }

  return { top, left };
};

const getExpiryStatus = (endDate) => {
  if (!endDate) return { status: 'invalid', color: 'gray' };
  
  const now = moment();
  const end = moment(endDate);
  const daysLeft = end.diff(now, 'days');
  
  if (daysLeft < 0) return { status: 'expired', color: 'red' };
  if (daysLeft <= 7) return { status: 'ending-soon', color: 'yellow' };
  return { status: 'active', color: 'green' };
};

const formatMoney = (amount) => {
  if (!amount) return 'N/A';
  return `₵${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const CampaignList = ({campaigns, fetchCampaigns}) => {
  const user = useSelector((state) => state?.user?.user) || null;
  const subscription_details = user?.subscription;
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeDropup, setActiveDropup] = useState(null);
  const itemsPerPage = 10;
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef({});
  const navigate = useNavigate();

  console.log(campaigns);

  const isSubscriptionExpired = () => {
    if (!subscription_details?.end_date) return true;
    const endDate = new Date(subscription_details.end_date);
    const currentDate = new Date();
    return endDate < currentDate;
  };

  const sortData = (data) => {
    if (!sortConfig.key || !data) return data;
    
    return [...data].sort((a, b) => {
      // Handle null/undefined values
      if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
      if (!a[sortConfig.key]) return 1;
      if (!b[sortConfig.key]) return -1;

      // Convert to lowercase for string comparison
      const aValue = typeof a[sortConfig.key] === 'string' 
        ? a[sortConfig.key].toLowerCase() 
        : a[sortConfig.key];
      const bValue = typeof b[sortConfig.key] === 'string' 
        ? b[sortConfig.key].toLowerCase() 
        : b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const requestSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = campaigns?.filter(item =>
      Object.entries(item).some(([key, value]) => {
        // Skip null/undefined values and non-searchable fields
        if (!value || typeof value === 'object') return false;
        
        // Convert value to string and check if it includes search term
        return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    ) || [];
    
    return sortData(filtered);
  }, [campaigns, searchTerm, sortConfig, sortData]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  //* Column headers
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    { key: 'location', label: 'Location' },
    { key: 'price', label: 'Price' },
    { key: 'discounted_price', label: 'Dsc.Price' },
    { key: 'start_date', label: 'Created' },
    { key: 'end_date', label: 'Expiry' },
    { key: 'approval_status', label: 'Status' },
    { key: 'delivery_status', label: 'Delivery' },
    { key: 'action', label: 'Action' },
  ];

  const [viewModalData, setViewModalData] = useState({
    isOpen: false,
    campaign: null
  });

  const handleCloseModal = () => {
    setViewModalData({ isOpen: false, campaign: null });
  };

  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString('en-US', options);
  };

  useEffect(() => {
    if (activeDropup) {
      const button = buttonRef.current[activeDropup];
      if (button) {
        const rect = button.getBoundingClientRect();
        const position = calculateDropdownPosition(rect);
        setDropdownPosition(position);
      }
    }
  }, [activeDropup]);

  useEffect(() => {
    const handleResize = () => {
      if (activeDropup) {
        const button = buttonRef.current[activeDropup];
        if (button) {
          const rect = button.getBoundingClientRect();
          const position = calculateDropdownPosition(rect);
          setDropdownPosition(position);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeDropup]);

  const handleEdit = (slug) => {
    navigate(`/deal/campaigns/edit/${slug}`);
  };

  const [categoryModalData, setCategoryModalData] = useState({
    isOpen: false,
    categories: [],
    title: ''
  });

  const handleCloseCategoryModal = () => {
    setCategoryModalData({ isOpen: false, categories: [], title: '' });
  };

  const [locationModalData, setLocationModalData] = useState({
    isOpen: false,
    locations: [],
    title: ''
  });

  const handleCloseLocationModal = () => {
    setLocationModalData({ isOpen: false, locations: [], title: '' });
  };

  const [deleteModalData, setDeleteModalData] = useState({
    isOpen: false,
    campaign: null
  });

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await campaignsRequest.deleteCampaign(deleteModalData.campaign.campaign_slug);
      console.log("RESPONSE", response);
      
      if (response.success) {
        toast.success(response?.data?.reason || "Campaign deleted successfully");
        setDeleteModalData({ isOpen: false, campaign: null });
        fetchCampaigns();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete campaign');
    } finally {
      setIsDeleting(false);
    }
  };

  const [rejectionModalData, setRejectionModalData] = useState({
    isOpen: false,
    reason: '',
    title: ''
  });

  const handleCloseRejectionModal = () => {
    setRejectionModalData({ isOpen: false, reason: '', title: '' });
  };

  // Add this useEffect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close dropdown if clicking outside
      if (activeDropup && !event.target.closest('.relative')) {
        setActiveDropup(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropup]);

  //* Navigate to blast interface
  const handleBlastCampaign = (campaign) => {
    if (isSubscriptionExpired()) {
      toast.error("Your subscription has expired. Please renew to blast campaigns.", {
        icon: "⚠️",
      });
      navigate("/pricing");
      return;
    }
    navigate(`/deal/campaigns/blast/${campaign.campaign_slug}`);
  };

  return (
    <>
    <div className="relative p-6 space-y-4 w-full bg-white rounded-xl shadow-sm">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2" size={20} />
        <input
          type="text"
          placeholder="Search campaigns..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="py-2 pr-4 pl-10 w-full rounded-xl border-2 border-gray-100 transition-all focus:border-pink-200 focus:ring-2 focus:ring-pink-100 focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto mt-10">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="bg-white border-b-2 border-gray-100">
              <th className="px-4 py-3 text-sm font-semibold text-left text-gray-600">Image</th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-4 py-3 text-sm font-semibold text-left text-gray-600 cursor-pointer hover:text-pink-500"
                  onClick={() => requestSort(column.key)}
                >
                  <div className="flex gap-2 items-center">
                    {column.label}
                    <ArrowUpDown size={14} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="relative">
            {paginatedData.map((campaign) => (
              <motion.tr
                key={campaign.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="border-b border-gray-50 hover:bg-gray-50/50"
              >
                <td className="px-4 py-3">
                  <div className="w-10 h-10 bg-gray-200 rounded">
                    <img 
                      src={campaign.image || 'https://placehold.co/100x100/FF6C6C/FFF?text=DB'} 
                      alt={campaign.title}
                      className='object-cover w-full h-full rounded-lg'
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://placehold.co/100x100/FF6C6C/FFF?text=DB';
                      }}
                    />
                  </div>
                </td>
                {columns.map((column) => (
                  <td key={column.key} className="relative px-4 py-3 text-sm text-gray-600">
                    {column.key === 'approval_status' ? (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        campaign[column.key] === "Approved" 
                          ? "bg-green-100 text-green-600"
                          : campaign[column.key] === "Pending"
                          ? "bg-yellow-100 text-yellow-600"
                          : "bg-red-100 text-red-600"
                      }`}>
                        {campaign[column.key] === "Approved" ? "Approved" : campaign[column.key] === "Pending" ? "Pending" : "Rejected"}
                      </span>
                    ) : column.key === 'delivery_status' ? (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        campaign[column.key] === "completed" 
                          ? "bg-blue-100 text-blue-600"
                          : campaign[column.key] === "partial"
                          ? "bg-purple-100 text-purple-600"
                          : "bg-rose-100 text-rose-600"
                      }`}>
                        {campaign[column.key] === "completed" 
                          ? "Completed" 
                          : campaign[column.key] === "partial" 
                          ? "Partial" 
                          : "Not Deployed"}
                      </span>
                    ) : column.key === 'action' ? (
                      <td className="relative">
                        <div className="inline-block relative">
                          <button
                            ref={el => buttonRef.current[campaign.id] = el}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent event bubbling
                              setActiveDropup(activeDropup === campaign.id ? null : campaign.id);
                            }}
                            className="p-1 rounded-lg transition-colors hover:bg-gray-100"
                          >
                            <MoreVertical size={18} />
                          </button>
                          <AnimatePresence>
                            {activeDropup === campaign.id && (
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                style={{ 
                                  position: 'fixed',
                                  top: dropdownPosition.top,
                                  left: dropdownPosition.left,
                                  zIndex: 1000
                                }}
                                className="bg-white rounded-xl shadow-lg py-2 min-w-[180px] border border-gray-100"
                                onClick={(e) => e.stopPropagation()} // Prevent event bubbling
                              >
                                <motion.button 
                                  whileHover={{ x: 4 }}
                                  onClick={() => {
                                    setViewModalData({
                                      isOpen: true,
                                      campaign: campaign
                                    });
                                    setActiveDropup(null);
                                  }}
                                  className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-50"
                                >
                                  <Eye size={16} className="text-blue-500" />
                                  View
                                </motion.button>
                                <motion.button
                                  onClick={() => handleEdit(campaign.campaign_slug)}
                                  whileHover={{ x: 4 }}
                                  className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-50"
                                >
                                  <Edit size={16} className="text-green-500" />
                                  Edit
                                </motion.button>
                                {campaign.approval_status?.toLowerCase() === 'approved' && 
                                  campaign.delivery_status?.toLowerCase() !== 'completed' &&
                                  moment(campaign.end_date).isAfter(moment()) &&
                                  !isSubscriptionExpired() && (
                                  <motion.button
                                    onClick={() => handleBlastCampaign(campaign)}
                                    whileHover={{ x: 4 }}
                                    className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-50"
                                  >
                                    <Megaphone size={16} className="text-purple-500" />
                                    Blast Campaign
                                  </motion.button>
                                )}
                                {campaign.approval_status === "Rejected" && (
                                  <>
                                    <motion.button 
                                      whileHover={{ x: 4 }}
                                      onClick={() => {
                                        setRejectionModalData({
                                          isOpen: true,
                                          reason: campaign.reject_reason || 'No reason provided',
                                          title: campaign.title
                                        });
                                        setActiveDropup(null);
                                      }}
                                      className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-gray-700 hover:bg-gray-50"
                                    >
                                      <X size={16} className="text-red-500" />
                                      Rejection Reason
                                    </motion.button>
                                    <div className="my-1 border-t border-gray-100"></div>
                                  </>
                                )}
                                <div className="my-1 border-t border-gray-100"></div>
                                <motion.button 
                                  whileHover={{ x: 4 }}
                                  onClick={() => {
                                    setDeleteModalData({
                                      isOpen: true,
                                      campaign: campaign
                                    });
                                    setActiveDropup(null);
                                  }}
                                  className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 size={16} />
                                  Delete
                                </motion.button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </td>
                    ) : column.key === 'category' ? (
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 min-w-0">
                          {campaign.category && campaign.category.length > 0 && (
                            <span
                              className="inline-block max-w-[150px] truncate px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FF6C6C]/10 text-[#FF6C6C]"
                              title={campaign.category[0]}
                            >
                              {campaign.category[0]}
                            </span>
                          )}
                        </div>
                        {campaign.category && campaign.category.length > 1 && (
                          <button
                            onClick={() => setCategoryModalData({
                              isOpen: true,
                              categories: campaign.category,
                              title: campaign.title
                            })}
                            className="flex-shrink-0 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-full px-2 py-0.5 transition-colors duration-200 whitespace-nowrap"
                          >
                            +{campaign.category.length - 1}
                          </button>
                        )}
                      </div>
                    ) : column.key === 'location' ? (
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 min-w-0">
                          {campaign.location && campaign.location.length > 0 && (
                            <span
                              className={`inline-block max-w-[150px] truncate px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                campaign.location[0].location === "all" 
                                  ? "bg-blue-50 text-blue-600" 
                                  : "bg-orange-50 text-orange-600"
                              }`}
                              title={campaign.location[0].location}
                            >
                              { campaign.location[0].location === "all" ? "All Locations" : `${campaign.location[0].location} - ${campaign?.location[0].region}` }
                            </span>
                          )}
                        </div>
                        {campaign.location && campaign.location.length > 1 && (
                          <button
                            onClick={() => setLocationModalData({
                              isOpen: true,
                              locations: campaign.location,
                              title: campaign.title
                            })}
                            className="flex-shrink-0 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-full px-2 py-0.5 transition-colors duration-200 whitespace-nowrap"
                          >
                            +{campaign.location.length - 1}
                          </button>
                        )}
                      </div>
                    ) : column.key === 'region' ? (
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 min-w-0">
                          {campaign.region && campaign.region.length > 0 && (
                            <span
                              className="inline-block max-w-[150px] truncate px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-600"
                              title={campaign.region[0]}
                            >
                              {campaign.region[0]}
                            </span>
                          )}
                        </div>
                        {campaign.region && campaign.region.length > 1 && (
                          <button
                            onClick={() =>
                              setViewModalData({
                                isOpen: true,
                                campaign: campaign
                              })
                            }
                            className="flex-shrink-0 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-full px-2 py-0.5 transition-colors duration-200 whitespace-nowrap"
                          >
                            +{campaign.region.length - 1}
                          </button>
                        )}
                      </div>
                    ): column.key === 'price' ? (
                      <div className="flex items-center">
                        <span className="text-sm font-medium">
                          {formatMoney(campaign[column.key])}
                        </span>
                      </div>
                    ) : column.key === 'discounted_price' ? (
                      <div className="flex items-center">
                        <span className="text-sm font-medium">
                          {formatMoney(campaign[column.key])}
                        </span>
                      </div>
                    ) : column.key === "start_date" ? (
                      <div className="flex items-center">
                        <span className="text-sm font-medium whitespace-nowrap">
                          {campaign[column.key] ? moment(campaign[column.key]).format('DD MMM, YYYY') : 'N/A'}
                        </span>
                      </div>
                    ) : column.key === "end_date" ? (
                      <div className="flex items-center">
                        {campaign[column.key] && (
                          <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap
                            ${(() => {
                              const { color } = getExpiryStatus(campaign[column.key]);
                              switch(color) {
                                case 'red': return 'bg-red-50 text-red-700';
                                case 'yellow': return 'bg-yellow-50 text-yellow-700';
                                case 'green': return 'bg-green-50 text-green-700';
                                default: return 'bg-gray-50 text-gray-700';
                              }
                            })()}`}
                          >
                            <span className="inline-block">{moment(campaign[column.key]).format('DD MMM, YYYY')}</span>
                            <span className="inline-block text-xs opacity-75">
                              {(() => {
                                const { status } = getExpiryStatus(campaign[column.key]);
                                switch(status) {
                                  case 'expired': return '(Expired)';
                                  case 'ending-soon': return '(Ending Soon)';
                                  case 'active': return '(Active)';
                                  default: return '';
                                }
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : column.key === "title"?(
                      <p className="text-sm font-medium">{`${campaign.title.length > 12 ? campaign.title.substring(0, 12) + "..." : campaign.title}`}</p>
                    )
                    :(
                      campaign[column.key]
                    )}
                  </td>
                ))}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center pt-4">
        <p className="text-sm text-gray-500">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} entries
        </p>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

    </div>
      <CampaignViewModal 
        isOpen={viewModalData.isOpen}
        onClose={handleCloseModal}
        campaign={viewModalData.campaign}
      />
      
      {/* Categories Modal */}
      <Modal
        isOpen={categoryModalData.isOpen}
        onClose={handleCloseCategoryModal}
        title={
          <div className="flex gap-2 items-center text-gray-700">
            <Tags className="w-5 h-5 text-[#FF6C6C]" />
            <span className="truncate">Categories for {categoryModalData.title}</span>
          </div>
        }
      >
        <div className="p-2 space-y-4 sm:p-6">
          <div className="flex flex-wrap gap-2 max-h-[60vh] overflow-y-auto">
            {categoryModalData.categories.map((category, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium bg-[#FF6C6C]/10 text-[#FF6C6C] transition-all hover:bg-[#FF6C6C]/20"
              >
                <Tags className="flex-shrink-0 w-4 h-4" />
                <span className="truncate">{category}</span>
              </div>
            ))}
          </div>
          <button
            onClick={handleCloseCategoryModal}
            className="flex gap-2 items-center px-4 py-2 mt-4 w-full text-sm text-gray-600 transition-colors sm:w-auto hover:text-gray-800"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </Modal>

      {/* Location Modal */}
      <Modal
        isOpen={locationModalData.isOpen}
        onClose={handleCloseLocationModal}
        title={
          <div className="flex gap-2 items-center text-gray-700">
            <MapPin className="w-5 h-5 text-orange-500" />
            <span className="truncate">Locations for {locationModalData.title}</span>
          </div>
        }
      >
        <div className="p-2 space-y-4 sm:p-6">
          <div className="flex flex-wrap gap-2 max-h-[60vh] overflow-y-auto">
            {locationModalData.locations.map((loc, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${
                  loc.location === "all"
                    ? "bg-blue-50 text-blue-600"
                    : "bg-orange-50 text-orange-600"
                } transition-all hover:bg-opacity-80`}
              >
                <MapPin className="flex-shrink-0 w-4 h-4" />
                {loc.location === "all" ? (
                  "All Locations"
                ) : (
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="truncate">{loc.location}</span>
                    <span className="text-gray-400">•</span>
                    <span className="text-xs bg-white/50 px-2 py-0.5 rounded-full truncate">
                      {loc.region}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={handleCloseLocationModal}
            className="flex gap-2 items-center px-4 py-2 mt-4 w-full text-sm text-gray-600 transition-colors sm:w-auto hover:text-gray-800"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </Modal>

      <DeleteModal
        isOpen={deleteModalData.isOpen}
        onClose={() => setDeleteModalData({ isOpen: false, campaign: null })}
        onDelete={handleDelete}
        title="Delete Campaign"
        itemName={deleteModalData.campaign?.title}
        isLoading={isDeleting}
      />

      <Modal
        isOpen={rejectionModalData.isOpen}
        onClose={handleCloseRejectionModal}
        title={
          <div className="flex gap-2 items-center text-gray-700">
            <X className="w-5 h-5 text-red-500" />
            <span className="truncate">Rejection Reason for {rejectionModalData.title}</span>
          </div>
        }
      >
        <div className="p-4 space-y-4 sm:p-6">
          <div className="p-4 bg-red-50 rounded-xl border border-red-100">
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-medium text-red-800">
                Campaign was rejected for the following reason:
              </h3>
              <p className="text-sm text-red-700 whitespace-pre-wrap">
                {rejectionModalData.reason}
              </p>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={handleCloseRejectionModal}
              className="flex gap-2 items-center px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
            >
              <X className="w-4 h-4" />
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CampaignList;